import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CheckmarkSvg from '@/components/common/CheckmarkSvg';
import ImportantSvg from '@/components/common/ImportantSvg';
import InfoTooltipSvg from '@/components/common/InfoTooltipSvg';
import { TmCart } from '@/tscapi/model/cart';
import { Cookie } from '@/tscapi/utils/cookie';

import { useAuthenticated } from './hooks/useAuthenticated';
import { useCart } from './hooks/useCart';
import { useOrderReview, useUpdatePromo } from './hooks/useOrderReview';

interface PromocodeProps {
    isModifyOrder: boolean;
}

export const Promocode = ({ isModifyOrder }: PromocodeProps) => {
    const cookie: Cookie = new Cookie();
    const isAuthenticated = useAuthenticated();
    const { data, isLoading } = useCart(isAuthenticated);
    const { data: orderReviewData, isFetching } = useOrderReview(isAuthenticated, cookie.ModifyOrderNumber, data);
    const [promoCode, setPromoCode] = useState('');
    const [lastShipCode, setLastShipCode] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isEmployeePromocodeEnabled, setIsEmployeePromocodeEnabled] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isValidPromo, setIsValidPromo] = useState(true);
    const [cartPromocode, setCartPromocode] = useState('');
    const mutation = useUpdatePromo(cookie.ModifyOrderNumber, data);
    const { t } = useTranslation();

    useEffect(() => {
        if (isModifyOrder) {
            if (cookie.ModifyOrderPromocode) {
                setCartPromocode(cookie.ModifyOrderPromocode);
                console.log('ran again');
                setPromoCode(cookie.ModifyOrderPromocode);
            }
        } else if (data) {
            //todo, look into this section.
            setCartPromocode(data.PromoCode);
            if (data.Buyer && data.Buyer.IsEmployee) {
                if (typeof window['employeePromocodeEnabled'] != 'undefined') {
                    setIsEmployeePromocodeEnabled(window['employeePromocodeEnabled']);
                }
            }
        }
    }, []);

    useEffect(() => {
        if (orderReviewData && orderReviewData.AppliedPromocode) {
            console.log('changed');
            setPromoCode(orderReviewData.AppliedPromocode);
        }
    }, [orderReviewData]);

    useEffect(() => {
        if (data?.ShipCode) {
            if (data?.ShipCode != lastShipCode) {
                //console.log('ship code changed, so clear the error message');
                setErrorMessage('');
                setLastShipCode(data.ShipCode);
            }
        }
    }, [data, lastShipCode]);

    const onPromoChange = (e) => {
        setErrorMessage('');
        setPromoCode(e.target.value);
    };
    const onPromoFocus = (e) => {
        setErrorMessage('');
        setIsValidPromo(true);
    };

    const applyCartPromoCode = async () => {
        //do a mutation
        mutation.mutate(promoCode, {
            onSuccess: () => {
                setEditMode(false);
            },
            onError: (error, variables, context) => {
                setErrorMessage(error as string);
                // cookie.deleteModifyOrder_Promocode();
                setTimeout(() => {
                    cookie.deleteModifyOrder_Promocode();
                }, 10);

                setIsValidPromo(false);
                console.log('error grabbed');
            },
        });
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        setErrorMessage('');
        setIsValidPromo(true);
        // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            applyCartPromoCode();
        }
    };

    if (!data) {
        return null;
    }

    return (
        <>
            {(!data.Buyer || (data.Buyer && (!data.Buyer.IsEmployee || isEmployeePromocodeEnabled))) && (
                <div className="promocode__container formWrap">
                    <>
                        {orderReviewData && orderReviewData.AppliedPromocode && !editMode ? (
                            <>
                                <div className="promo__row-wrap promo__applied">
                                    <div className="promo__applied-left">
                                        <CheckmarkSvg className="promo__svg--checkmark" />
                                        <div className="promocode__code">{orderReviewData.AppliedPromocode}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className="promo__button-remove"
                                        tabIndex={0}
                                        onClick={() => {
                                            setEditMode(true);
                                        }}
                                    >
                                        {t('Replace')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="promo__row-wrap" style={{ marginTop: '10px' }}>
                                    <div className="promo__input-container ">
                                        <input
                                            className="promo__input"
                                            autoComplete="off"
                                            id="promo"
                                            name="promocode"
                                            data-valid={isValidPromo}
                                            data-empty={promoCode !== '' ? false : true}
                                            value={promoCode ? promoCode.toUpperCase() : ''}
                                            //onBlur={onPromoBlur}
                                            onChange={onPromoChange}
                                            onFocus={onPromoFocus}
                                            onKeyDown={onKeyDown}
                                        />
                                        <label htmlFor="promo" className="promo__floating-label">
                                            {t('Enter promo code')}
                                        </label>
                                    </div>
                                    <button
                                        className="promo__button--white"
                                        onClick={applyCartPromoCode}
                                        data-link-type="link"
                                        data-link-title="promo code"
                                        tabIndex={0}
                                        data-component-type="express checkout"
                                    >
                                        {t('Apply')}
                                    </button>
                                </div>
                                {!isValidPromo && <div className="promo__validation">{errorMessage}</div>}
                                <div className="promo__row-wrap" style={{ marginBottom: '0' }}>
                                    <ImportantSvg className="promo__info-icon" />
                                    <div className="promo__message">
                                        <b>{t('Important!')}</b> {t('Only one promo code can be used at a time.')}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                </div>
            )}
        </>
    );
};
